/**
 * Accurate iPhone 16 Pro-spec phone frame.
 * All measurements from spec sheet, scaled via --iphone-scale (default 4px/unit).
 *
 * Body:    71.45 × 149.61 units → 285.8 × 598.4 px at scale 4
 * Display: 66.57 × 144.79 units (bezel: 2.44 units all around)
 * Island:  20.73 units wide, centered, 6.07 units from display top
 */

const SCALE = 4.8;
const u = (v: number) => v * SCALE; // unit → px

const BODY_W  = u(71.45);   // 285.8
const BODY_H  = u(149.61);  // 598.4
const RADIUS  = u(11.5);    // slightly rounded → 46px
const BEZEL   = u(2.44);    // 9.76 — display inset from body edge
const DISP_W  = u(66.57);   // 266.3
const DISP_H  = u(144.79);  // 579.2
const ISLAND_W = u(20.73);  // 82.9
const ISLAND_H = u(5.8);    // 23.2
const ISLAND_CX = u(35.72); // 142.9 — center from body left
const ISLAND_TOP = u(2.5);  // moved up, close to top edge
const BTN_PROTRUDE = u(0.45); // 1.8

// Frame colors — match project-h-landing: bg-foreground/5 body, bg-foreground/80 island & buttons
const FRAME_BODY   = "color-mix(in oklch, var(--color-foreground) 5%, transparent)";
const FRAME_DARK   = "color-mix(in oklch, var(--color-foreground) 80%, transparent)";
const FRAME_BTN    = "color-mix(in oklch, var(--color-foreground) 18%, transparent)";
const FRAME_BORDER = "color-mix(in oklch, var(--color-border) 60%, transparent)";
// Inner display screen radius (body radius minus bezel)
const DISP_R = RADIUS - BEZEL;

// Left-side buttons: [topFromBodyTop (units), heightUnits, label]
const LEFT_BTNS: [number, number, string][] = [
  [24.5, 6,  "action"],
  [44,   18, "vol-up"],
  [66,   18, "vol-down"],
];
// Right-side buttons
const RIGHT_BTNS: [number, number, string][] = [
  [38, 22, "power"],
];

interface Props {
  children: React.ReactNode;
  className?: string;
}

export default function PhoneFrame({ children, className = "" }: Props) {
  return (
    // Outer wrapper adds horizontal space for side button protrusion
    <div
      className={`relative shrink-0 ${className}`}
      style={{ width: BODY_W + BTN_PROTRUDE * 2, height: BODY_H }}
    >
      {/* ── Body ── */}
      <div
        className="absolute"
        style={{
          left: BTN_PROTRUDE,
          top: 0,
          width: BODY_W,
          height: BODY_H,
          borderRadius: RADIUS,
          backgroundColor: FRAME_BODY,
          boxShadow: `0 0 0 1px ${FRAME_BORDER}, 0 24px_64px_-12px_rgba(0,0,0,0.15)`,
          outline: `1px solid ${FRAME_BORDER}`,
        }}
      >
        {/* ── Display (active area) ── */}
        <div
          className="absolute overflow-hidden bg-card"
          style={{
            left: BEZEL,
            top: BEZEL,
            width: DISP_W,
            height: DISP_H,
            borderRadius: DISP_R,
          }}
        >
          {children}

          {/* ── Dynamic Island ── */}
          <div
            className="absolute z-10"
            style={{
              top: ISLAND_TOP,
              left: ISLAND_CX - BTN_PROTRUDE - BEZEL - ISLAND_W / 2,
              width: ISLAND_W,
              height: ISLAND_H,
              borderRadius: ISLAND_H / 2,
              backgroundColor: FRAME_DARK,
            }}
          />
        </div>
      </div>

      {/* ── Left buttons ── */}
      {LEFT_BTNS.map(([top, h, key]) => (
        <div
          key={key}
          className="absolute"
          style={{
            left: 0,
            top: u(top),
            width: BTN_PROTRUDE + 1,
            height: u(h),
            borderRadius: "2px 0 0 2px",
            backgroundColor: FRAME_BODY,
          }}
        />
      ))}

      {/* ── Right buttons ── */}
      {RIGHT_BTNS.map(([top, h, key]) => (
        <div
          key={key}
          className="absolute"
          style={{
            right: 0,
            top: u(top),
            width: BTN_PROTRUDE + 1,
            height: u(h),
            borderRadius: "0 2px 2px 0",
            backgroundColor: FRAME_BODY,
          }}
        />
      ))}
    </div>
  );
}
